// S10 — tenant detail, Support tab (spec 15 §3.7, §3.8, §4 S10; M15-8,
// D-620). From one tenant's page a support staff member finds a person, opens
// the tenant as them under a time-boxed, audited grant, mails them their own
// reset link, or signs them out. Live grants show their countdown with Open,
// Approve and End; the history lists every grant with its changes made. A
// platform_admin can sign everyone out for a security incident, with the
// subdomain typed out.
//
// Every write holds its Idempotency-Key until the server answers it
// (lib/held-key.ts). The role rules mirror the server's (lib/support.ts): the
// console never offers a control the server refuses, and the server refuses
// regardless. A sandbox member reads a fleet tenant and changes nothing on it.

import { useEffect, useState, type ReactNode } from "react";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { PageHeader } from "../../../components/breadcrumbs";
import { Button } from "../../../components/button";
import { Card } from "../../../components/card";
import { DataTable, type DataTableColumn } from "../../../components/data-table";
import { Field } from "../../../components/field";
import { Modal } from "../../../components/modal";
import { RowMenu } from "../../../components/row-menu";
import { Select } from "../../../components/select";
import { CardSkeleton, EmptyState, ErrorState, TableSkeleton } from "../../../components/states";
import { StatusChip, type ChipVariant } from "../../../components/status-chip";
import { Banner } from "../../../components/toast";
import { formatTimestamp } from "../../../lib/datetime";
import { useDebouncedValue } from "../../../lib/use-debounced-value";
import { requestIdOf, useSignInAgainOn401 } from "../components/console-frame";
import { TenantTabs } from "../components/tenant-tabs";
import { useHeldKey, withHeldKey } from "../lib/held-key";
import { getTenant, PlatformError, type PlatformMe, type TenantDetail } from "../lib/platform-api";
import {
  approveGrant,
  createGrant,
  endGrant,
  listGrants,
  listTenantUsers,
  mintGrantToken,
  revokeTenantSessions,
  revokeUserSessions,
  sendUserPasswordReset,
  type Grant,
  type TenantUser,
} from "../lib/support-api";
import {
  canActAsAdmin,
  canReadAudit,
  canUseSupportTools,
  grantActions,
  grantModes,
  minutesLeft,
  needsApproval,
  notEligibleReason,
  supportFailure,
} from "../lib/support";

/** The durations S10 offers, in minutes (§3.7 step 1: at most 480). */
export const GRANT_DURATIONS = [15, 30, 60, 120, 240, 480] as const;
const HISTORY_PAGE = 25;

function tenantQueryKey(id: string) {
  return ["platform", "tenant", id] as const;
}

function grantsKey(tenantId: string) {
  return ["platform", "grants", tenantId] as const;
}

/** A clock that moves every 30 seconds, for the countdowns. */
function useNow(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => {
      setNow(new Date());
    }, 30_000);
    return () => {
      clearInterval(timer);
    };
  }, []);
  return now;
}

/** The sentence a refused write earns. */
export function supportMessage(error: unknown, t: TFunction, email?: string): string {
  const kind = supportFailure(error);
  if (kind === "not_eligible") {
    const reason = notEligibleReason(error);
    if (
      reason === "email_unverified" ||
      reason === "user_not_active" ||
      reason === "not_a_person"
    ) {
      return t(`admin.support.resetNotEligible.${reason}`, { email: email ?? "" });
    }
  }
  return t(`admin.support.failure.${kind}`);
}

function grantVariant(status: Grant["status"]): ChipVariant {
  switch (status) {
    case "active":
      return "ok";
    case "pending_approval":
      return "warn";
    case "revoked":
      return "bad";
    default:
      return "neutral";
  }
}

type Notice = { kind: "ok" | "bad"; text: string } | null;

export function TenantSupportScreen({ me, tenantId }: { me: PlatformMe; tenantId: string }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const detail = useQuery({
    queryKey: tenantQueryKey(tenantId),
    queryFn: ({ signal }) => getTenant(tenantId, signal),
  });
  useSignInAgainOn401(detail.error);

  if (detail.isPending) {
    return (
      <section className="admin-screen" data-testid="admin-tenant-support-loading">
        <PageHeader
          title={t("admin.tenant.loading")}
          crumbs={[{ label: t("admin.tenant.back"), to: "/console/tenants" }]}
        />
        <CardSkeleton lines={6} label={t("admin.tenant.loading")} />
      </section>
    );
  }
  if (detail.isError) {
    const notFound =
      detail.error instanceof PlatformError &&
      (detail.error.status === 404 || detail.error.status === 400);
    return (
      <section className="admin-screen" data-testid="admin-tenant-support-error">
        <PageHeader
          title={notFound ? t("admin.tenant.notFoundTitle") : t("admin.load.failed")}
          crumbs={[{ label: t("admin.tenant.back"), to: "/console/tenants" }]}
        />
        {notFound ? (
          <EmptyState
            title={t("admin.tenant.notFoundTitle")}
            body={t("admin.tenant.notFoundBody")}
            ctaLabel={t("admin.tenant.back")}
            onCta={() => {
              void navigate({ to: "/console/tenants" });
            }}
          />
        ) : (
          <ErrorState
            title={t("admin.load.failed")}
            retryLabel={t("admin.load.retry")}
            onRetry={() => {
              void detail.refetch();
            }}
            requestId={requestIdOf(detail.error)}
            requestIdLabel={t("admin.load.requestId")}
          />
        )}
      </section>
    );
  }
  return <SupportBody me={me} view={detail.data} />;
}

function SupportBody({ me, view }: { me: PlatformMe; view: TenantDetail }) {
  const { t } = useTranslation();
  const tenant = view.tenant;
  const tools = canUseSupportTools(me.role);
  // A sandbox member reads a fleet tenant and changes nothing (D-477 ruling 3).
  const fleetOnly = me.scope === "sandbox" && !tenant.sandbox;
  const canWrite = tools && !fleetOnly;
  const [notice, setNotice] = useState<Notice>(null);

  return (
    <section className="admin-screen" data-testid="admin-tenant-support">
      <PageHeader
        title={tenant.name}
        crumbs={[{ label: t("admin.tenant.back"), to: "/console/tenants" }]}
        actions={
          canReadAudit(me.role) ? (
            <Link
              to="/console/audit"
              search={{ tenant: tenant.id }}
              className="admin-link"
              data-testid="admin-support-audit-link"
            >
              {t("admin.support.seeAudit")}
            </Link>
          ) : undefined
        }
      />
      <TenantTabs tenantId={tenant.id} />
      <p className="admin-card-lead">{t("admin.support.lead")}</p>
      {fleetOnly ? (
        <Banner kind="neutral" data-testid="admin-support-sandbox-only">
          {t("admin.tenant.sandboxOnly")}
        </Banner>
      ) : null}
      {!tools ? (
        <Banner kind="neutral" data-testid="admin-support-viewer">
          {t("admin.support.viewerNote")}
        </Banner>
      ) : null}
      {notice !== null ? (
        <Banner
          kind={notice.kind}
          data-testid="admin-support-notice"
          onDismiss={() => {
            setNotice(null);
          }}
          dismissLabel={t("admin.support.close")}
        >
          {notice.text}
        </Banner>
      ) : null}
      <div className="admin-card-stack">
        {tools ? <PeopleCard me={me} view={view} canWrite={canWrite} onNotice={setNotice} /> : null}
        <LiveGrantsCard me={me} view={view} canWrite={canWrite} onNotice={setNotice} />
        <HistoryCard tenantId={tenant.id} />
        {canActAsAdmin(me.role) && canWrite ? (
          <DangerCard view={view} onNotice={setNotice} />
        ) : null}
      </div>
    </section>
  );
}

function PeopleCard({
  me,
  view,
  canWrite,
  onNotice,
}: {
  me: PlatformMe;
  view: TenantDetail;
  canWrite: boolean;
  onNotice: (n: Notice) => void;
}) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const tenantId = view.tenant.id;
  const [search, setSearch] = useState("");
  const q = useDebouncedValue(search.trim(), 300);
  const people = useQuery({
    queryKey: ["platform", "tenant-users", tenantId, q],
    queryFn: ({ signal }) => listTenantUsers(tenantId, q, signal),
  });
  useSignInAgainOn401(people.error);
  const [grantFor, setGrantFor] = useState<TenantUser | null>(null);
  const [revokeFor, setRevokeFor] = useState<TenantUser | null>(null);
  const resetKey = useHeldKey();
  const reset = useMutation({
    mutationFn: (user: TenantUser) =>
      withHeldKey(resetKey, { reset: user.id }, (key) =>
        sendUserPasswordReset(tenantId, user.id, key),
      ),
    onSuccess: (_void, user) => {
      onNotice({ kind: "ok", text: t("admin.support.resetSent", { email: user.email }) });
    },
    onError: (error, user) => {
      onNotice({ kind: "bad", text: supportMessage(error, t, user.email) });
    },
  });
  useSignInAgainOn401(reset.error);

  const columns: DataTableColumn<TenantUser>[] = [
    {
      key: "name",
      header: t("admin.support.colName"),
      render: (row) => (
        <span className="admin-name-cell">
          <span className="admin-strong">{row.display_name}</span>
          {row.locked ? (
            <StatusChip variant="bad" data-testid="admin-support-locked">
              {t("admin.support.locked")}
            </StatusChip>
          ) : null}
        </span>
      ),
    },
    { key: "email", header: t("admin.support.colEmail"), render: (row) => row.email },
    {
      key: "roles",
      header: t("admin.support.colRoles"),
      render: (row) => row.roles.map((r) => r.name).join(", "),
    },
    {
      key: "last",
      header: t("admin.support.colLastLogin"),
      render: (row) =>
        row.last_login_at === null
          ? t("admin.support.never")
          : formatTimestamp(row.last_login_at, i18n.language),
    },
  ];
  if (canWrite) {
    columns.push({
      key: "actions",
      header: "",
      actions: true,
      render: (row) => (
        <RowMenu
          label={t("admin.support.actionsFor", { name: row.display_name })}
          data-testid="admin-support-person-menu"
        >
          <button
            type="button"
            data-testid="admin-support-impersonate"
            onClick={() => {
              setGrantFor(row);
            }}
          >
            {t("admin.support.impersonate")}
          </button>
          <button
            type="button"
            data-testid="admin-support-reset"
            onClick={() => {
              onNotice(null);
              reset.mutate(row);
            }}
          >
            {t("admin.support.sendReset")}
          </button>
          <button
            type="button"
            data-testid="admin-support-revoke"
            onClick={() => {
              setRevokeFor(row);
            }}
          >
            {t("admin.support.revokeSessions")}
          </button>
        </RowMenu>
      ),
    });
  }

  let body: ReactNode;
  if (people.isPending) {
    body = <TableSkeleton rows={4} cols={4} label={t("admin.support.loading")} />;
  } else if (people.isError) {
    body = (
      <ErrorState
        title={t("admin.support.loadFailed")}
        retryLabel={t("admin.load.retry")}
        onRetry={() => {
          void people.refetch();
        }}
        requestId={requestIdOf(people.error)}
        requestIdLabel={t("admin.load.requestId")}
      />
    );
  } else if (people.data.length === 0) {
    body = (
      <p className="admin-muted" data-testid="admin-support-people-empty">
        {q === "" ? t("admin.support.emptyAll") : t("admin.support.empty")}
      </p>
    );
  } else {
    body = (
      <DataTable
        columns={columns}
        rows={people.data}
        rowKey={(row) => row.id}
        empty={null}
        caption={t("admin.support.people")}
        rowTestId="admin-support-person"
        rowProps={(row) => ({ "data-email": row.email })}
      />
    );
  }

  return (
    <Card title={t("admin.support.people")} data-testid="admin-support-people">
      <div className="admin-filters">
        <Field label={t("admin.support.search")} wide>
          <input
            className="ds-input"
            type="search"
            value={search}
            placeholder={t("admin.support.searchPlaceholder")}
            data-testid="admin-support-search"
            onChange={(e) => {
              setSearch(e.target.value);
            }}
          />
        </Field>
      </div>
      {body}
      {grantFor !== null ? (
        <GrantDialog
          me={me}
          view={view}
          user={grantFor}
          onClose={() => {
            setGrantFor(null);
          }}
          onChanged={() => {
            void queryClient.invalidateQueries({ queryKey: grantsKey(tenantId) });
          }}
        />
      ) : null}
      {revokeFor !== null ? (
        <RevokeUserDialog
          tenantId={tenantId}
          user={revokeFor}
          onClose={() => {
            setRevokeFor(null);
          }}
          onDone={(count) => {
            onNotice({
              kind: "ok",
              text: t("admin.support.revoked", { name: revokeFor.display_name, count }),
            });
            setRevokeFor(null);
          }}
        />
      ) : null}
    </Card>
  );
}

/** Open a tab now, while the click is still a user gesture, and point it at
 * the one-time link once the server has minted it. */
async function openTenantTab(grantId: string, key: string): Promise<void> {
  const tab = window.open("about:blank", "_blank");
  try {
    const url = await mintGrantToken(grantId, key);
    if (tab !== null) {
      tab.opener = null;
      tab.location.href = url;
    } else {
      window.location.assign(url);
    }
  } catch (error) {
    tab?.close();
    throw error;
  }
}

function GrantDialog({
  me,
  view,
  user,
  onClose,
  onChanged,
}: {
  me: PlatformMe;
  view: TenantDetail;
  user: TenantUser;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const modes = grantModes(me.role);
  const [mode, setMode] = useState<"read_only" | "act_as">(modes[0] ?? "read_only");
  const [reason, setReason] = useState("");
  const [ticket, setTicket] = useState("");
  const [minutes, setMinutes] = useState<number>(60);
  const [created, setCreated] = useState<{ id: string; pending: boolean } | null>(null);
  const createKey = useHeldKey();
  const openKey = useHeldKey();
  const body = {
    tenant_id: view.tenant.id,
    target_user_id: user.id,
    mode,
    reason: reason.trim(),
    ticket_ref: ticket.trim(),
    duration_minutes: minutes,
  };
  const create = useMutation({
    mutationFn: () => withHeldKey(createKey, body, (key) => createGrant(body, key)),
    onSuccess: (result) => {
      setCreated({ id: result.grant.id, pending: result.approval_required });
      onChanged();
    },
  });
  const open = useMutation({
    mutationFn: (grantId: string) =>
      withHeldKey(openKey, { open: grantId }, (key) => openTenantTab(grantId, key)),
  });
  useSignInAgainOn401(create.error);
  useSignInAgainOn401(open.error);
  const valid = body.reason.length >= 10 && body.ticket_ref.length >= 1;

  return (
    <Modal
      label={t("admin.support.grantTitle", { name: user.display_name })}
      heading={t("admin.support.grantTitle", { name: user.display_name })}
      closeLabel={t("admin.support.close")}
      onClose={onClose}
      pending={create.isPending}
      testId="admin-grant-dialog"
      className="admin-modal"
    >
      {created === null ? (
        <form
          className="admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (valid) create.mutate();
          }}
        >
          <Field label={t("admin.support.mode")}>
            <Select
              value={mode}
              data-testid="admin-grant-mode"
              onChange={(e) => {
                setMode(e.target.value as "read_only" | "act_as");
              }}
            >
              {modes.map((m) => (
                <option key={m} value={m}>
                  {t(`admin.support.modes.${m}`)}
                </option>
              ))}
            </Select>
          </Field>
          <p className="admin-muted">{t(`admin.support.modeHelp.${mode}`)}</p>
          {needsApproval(me.role, mode) ? (
            <Banner kind="warn" data-testid="admin-grant-approval-note">
              {t("admin.support.approvalNote")}
            </Banner>
          ) : null}
          <Field label={t("admin.support.reason")} wide>
            <textarea
              className="ds-input"
              rows={3}
              value={reason}
              maxLength={2000}
              data-testid="admin-grant-reason"
              onChange={(e) => {
                setReason(e.target.value);
              }}
            />
          </Field>
          <p className="admin-muted">{t("admin.support.reasonHint")}</p>
          <Field label={t("admin.support.ticket")}>
            <input
              className="ds-input"
              value={ticket}
              maxLength={200}
              placeholder={t("admin.support.ticketPlaceholder")}
              data-testid="admin-grant-ticket"
              onChange={(e) => {
                setTicket(e.target.value);
              }}
            />
          </Field>
          <Field label={t("admin.support.duration")}>
            <Select
              value={String(minutes)}
              data-testid="admin-grant-duration"
              onChange={(e) => {
                setMinutes(Number(e.target.value));
              }}
            >
              {GRANT_DURATIONS.map((m) => (
                <option key={m} value={m}>
                  {t("admin.support.durationOption", { minutes: m })}
                </option>
              ))}
            </Select>
          </Field>
          {create.isError ? (
            <Banner kind="bad" data-testid="admin-grant-error">
              {supportMessage(create.error, t)}
            </Banner>
          ) : null}
          <div className="admin-actions">
            <Button
              type="submit"
              variant="primary"
              disabled={!valid}
              busy={create.isPending}
              busyLabel={t("admin.support.requesting")}
              data-testid="admin-grant-submit"
            >
              {t("admin.support.request")}
            </Button>
            <Button onClick={onClose}>{t("admin.support.cancel")}</Button>
          </div>
        </form>
      ) : (
        <div className="admin-form" data-testid="admin-grant-result">
          {created.pending ? (
            <Banner kind="warn" data-testid="admin-grant-pending">
              {t("admin.support.pending")}
            </Banner>
          ) : (
            <>
              <Banner kind="ok" data-testid="admin-grant-granted">
                {t("admin.support.granted")}
              </Banner>
              <p className="admin-muted">{t("admin.support.openNote")}</p>
            </>
          )}
          {open.isError ? (
            <Banner kind="bad" data-testid="admin-grant-open-error">
              {supportMessage(open.error, t)}
            </Banner>
          ) : null}
          <div className="admin-actions">
            {!created.pending ? (
              <Button
                variant="primary"
                busy={open.isPending}
                busyLabel={t("admin.support.opening")}
                data-testid="admin-grant-open"
                onClick={() => {
                  open.mutate(created.id);
                }}
              >
                {t("admin.support.open")}
              </Button>
            ) : null}
            <Button onClick={onClose} data-testid="admin-grant-done">
              {t("admin.support.close")}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

function RevokeUserDialog({
  tenantId,
  user,
  onClose,
  onDone,
}: {
  tenantId: string;
  user: TenantUser;
  onClose: () => void;
  onDone: (count: number) => void;
}) {
  const { t } = useTranslation();
  const key = useHeldKey();
  const revoke = useMutation({
    mutationFn: () =>
      withHeldKey(key, { revoke: user.id }, (k) => revokeUserSessions(tenantId, user.id, k)),
    onSuccess: onDone,
  });
  useSignInAgainOn401(revoke.error);
  return (
    <Modal
      label={t("admin.support.revokeTitle", { name: user.display_name })}
      heading={t("admin.support.revokeTitle", { name: user.display_name })}
      closeLabel={t("admin.support.close")}
      onClose={onClose}
      pending={revoke.isPending}
      testId="admin-revoke-dialog"
      className="admin-modal"
    >
      <p className="admin-card-lead">
        {t("admin.support.revokeBody", { name: user.display_name })}
      </p>
      {revoke.isError ? (
        <Banner kind="bad" data-testid="admin-revoke-error">
          {supportMessage(revoke.error, t)}
        </Banner>
      ) : null}
      <div className="admin-actions">
        <Button
          variant="danger"
          busy={revoke.isPending}
          busyLabel={t("admin.support.ending")}
          data-testid="admin-revoke-confirm"
          onClick={() => {
            revoke.mutate();
          }}
        >
          {t("admin.support.revokeConfirm")}
        </Button>
        <Button onClick={onClose}>{t("admin.support.cancel")}</Button>
      </div>
    </Modal>
  );
}

function LiveGrantsCard({
  me,
  view,
  canWrite,
  onNotice,
}: {
  me: PlatformMe;
  view: TenantDetail;
  canWrite: boolean;
  onNotice: (n: Notice) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const tenantId = view.tenant.id;
  const now = useNow();
  const live = useQuery({
    queryKey: [...grantsKey(tenantId), "live"],
    queryFn: async ({ signal }) => {
      const [active, pending] = await Promise.all([
        listGrants({ tenant_id: tenantId, status: "active", limit: 100 }, signal),
        listGrants({ tenant_id: tenantId, status: "pending_approval", limit: 100 }, signal),
      ]);
      return [...pending.items, ...active.items];
    },
    refetchInterval: 30_000,
  });
  useSignInAgainOn401(live.error);
  const actKey = useHeldKey();
  const act = useMutation({
    mutationFn: (input: { grant: Grant; what: "approve" | "end" | "open" }) =>
      withHeldKey(actKey, { id: input.grant.id, what: input.what }, async (key) => {
        if (input.what === "approve") await approveGrant(input.grant.id, key);
        else if (input.what === "end") await endGrant(input.grant.id, key);
        else await openTenantTab(input.grant.id, key);
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: grantsKey(tenantId) });
    },
    onError: (error) => {
      onNotice({ kind: "bad", text: supportMessage(error, t) });
    },
  });
  useSignInAgainOn401(act.error);

  const columns: DataTableColumn<Grant>[] = [
    { key: "staff", header: t("admin.support.colStaff"), render: (g) => g.staff_display_name },
    {
      key: "person",
      header: t("admin.support.colPerson"),
      render: (g) => g.target_display_name ?? t("admin.support.unknownPerson"),
    },
    {
      key: "mode",
      header: t("admin.support.colMode"),
      render: (g) => t(`admin.support.modes.${g.mode}`),
    },
    { key: "ticket", header: t("admin.support.colTicket"), render: (g) => g.ticket_ref },
    {
      key: "status",
      header: t("admin.support.colStatus"),
      render: (g) => (
        <StatusChip variant={grantVariant(g.status)} data-testid="admin-grant-status">
          {t(`admin.support.grantStatus.${g.status}`)}
        </StatusChip>
      ),
    },
    {
      key: "left",
      header: t("admin.support.colLeft"),
      numeric: true,
      render: (g) => t("admin.support.minutesLeft", { minutes: minutesLeft(g.expires_at, now) }),
    },
  ];
  if (canWrite || me.role === "platform_admin") {
    columns.push({
      key: "actions",
      header: "",
      actions: true,
      render: (g) => {
        const can = grantActions(me, g);
        return (
          <span className="admin-inline">
            {can.open && canWrite ? (
              <Button
                size="sm"
                variant="primary"
                data-testid="admin-grant-row-open"
                onClick={() => {
                  act.mutate({ grant: g, what: "open" });
                }}
              >
                {t("admin.support.open")}
              </Button>
            ) : null}
            {can.approve && canWrite ? (
              <Button
                size="sm"
                data-testid="admin-grant-row-approve"
                onClick={() => {
                  act.mutate({ grant: g, what: "approve" });
                }}
              >
                {t("admin.support.approve")}
              </Button>
            ) : null}
            {can.end && canWrite ? (
              <Button
                size="sm"
                variant="danger"
                data-testid="admin-grant-row-end"
                onClick={() => {
                  act.mutate({ grant: g, what: "end" });
                }}
              >
                {t("admin.support.end")}
              </Button>
            ) : null}
          </span>
        );
      },
    });
  }

  let body: ReactNode;
  if (live.isPending) {
    body = <TableSkeleton rows={2} cols={5} label={t("admin.support.liveTitle")} />;
  } else if (live.isError) {
    body = (
      <ErrorState
        title={t("admin.support.historyFailed")}
        retryLabel={t("admin.load.retry")}
        onRetry={() => {
          void live.refetch();
        }}
        requestId={requestIdOf(live.error)}
        requestIdLabel={t("admin.load.requestId")}
      />
    );
  } else if (live.data.length === 0) {
    body = (
      <p className="admin-muted" data-testid="admin-support-live-empty">
        {t("admin.support.liveEmpty")}
      </p>
    );
  } else {
    body = (
      <DataTable
        columns={columns}
        rows={live.data}
        rowKey={(g) => g.id}
        empty={null}
        caption={t("admin.support.liveTitle")}
        rowTestId="admin-grant-live-row"
        rowProps={(g) => ({ "data-ticket": g.ticket_ref, "data-status": g.status })}
      />
    );
  }
  return (
    <Card title={t("admin.support.liveTitle")} data-testid="admin-support-live">
      {body}
    </Card>
  );
}

function HistoryCard({ tenantId }: { tenantId: string }) {
  const { t, i18n } = useTranslation();
  const history = useInfiniteQuery({
    queryKey: [...grantsKey(tenantId), "history"],
    queryFn: ({ pageParam, signal }) =>
      listGrants(
        {
          tenant_id: tenantId,
          limit: HISTORY_PAGE,
          ...(pageParam !== undefined ? { cursor: pageParam } : {}),
        },
        signal,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  useSignInAgainOn401(history.error);
  const columns: DataTableColumn<Grant>[] = [
    { key: "staff", header: t("admin.support.colStaff"), render: (g) => g.staff_display_name },
    {
      key: "person",
      header: t("admin.support.colPerson"),
      render: (g) => g.target_display_name ?? t("admin.support.unknownPerson"),
    },
    {
      key: "mode",
      header: t("admin.support.colMode"),
      render: (g) => t(`admin.support.modes.${g.mode}`),
    },
    { key: "ticket", header: t("admin.support.colTicket"), render: (g) => g.ticket_ref },
    {
      key: "status",
      header: t("admin.support.colStatus"),
      render: (g) => (
        <StatusChip variant={grantVariant(g.status)}>
          {t(`admin.support.grantStatus.${g.status}`)}
        </StatusChip>
      ),
    },
    {
      key: "started",
      header: t("admin.support.colStarted"),
      render: (g) => formatTimestamp(g.starts_at, i18n.language),
    },
    {
      key: "changes",
      header: t("admin.support.colChanges"),
      numeric: true,
      render: (g) => g.actions_count,
    },
  ];

  let body: ReactNode;
  if (history.isPending) {
    body = <TableSkeleton rows={3} cols={6} label={t("admin.support.historyTitle")} />;
  } else if (history.isError) {
    body = (
      <ErrorState
        title={t("admin.support.historyFailed")}
        retryLabel={t("admin.load.retry")}
        onRetry={() => {
          void history.refetch();
        }}
        requestId={requestIdOf(history.error)}
        requestIdLabel={t("admin.load.requestId")}
      />
    );
  } else {
    const rows = history.data.pages.flatMap((p) => p.items);
    body =
      rows.length === 0 ? (
        <p className="admin-muted" data-testid="admin-support-history-empty">
          {t("admin.support.historyEmpty")}
        </p>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(g) => g.id}
            empty={null}
            caption={t("admin.support.historyTitle")}
            rowTestId="admin-grant-history-row"
            rowProps={(g) => ({ "data-ticket": g.ticket_ref, "data-status": g.status })}
          />
          {history.hasNextPage ? (
            <Button
              size="sm"
              busy={history.isFetchingNextPage}
              data-testid="admin-support-history-more"
              onClick={() => {
                void history.fetchNextPage();
              }}
            >
              {t("admin.support.historyMore")}
            </Button>
          ) : null}
        </>
      );
  }
  return (
    <Card title={t("admin.support.historyTitle")} data-testid="admin-support-history">
      {body}
    </Card>
  );
}

function DangerCard({ view, onNotice }: { view: TenantDetail; onNotice: (n: Notice) => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const key = useHeldKey();
  const tenant = view.tenant;
  const revoke = useMutation({
    mutationFn: () =>
      withHeldKey(key, { all: tenant.id, typed }, (k) => revokeTenantSessions(tenant.id, typed, k)),
    onSuccess: (result) => {
      setOpen(false);
      setTyped("");
      onNotice({
        kind: "ok",
        text: t("admin.support.revokeAllDone", {
          sessions: result.sessions_revoked,
          grants: result.grants_revoked,
        }),
      });
      void queryClient.invalidateQueries({ queryKey: grantsKey(tenant.id) });
    },
  });
  useSignInAgainOn401(revoke.error);
  const matches = typed.trim().toLowerCase() === tenant.subdomain.toLowerCase();
  return (
    <Card title={t("admin.support.dangerTitle")} data-testid="admin-support-danger">
      <p className="admin-muted">{t("admin.support.dangerBody")}</p>
      <div className="admin-actions">
        <Button
          variant="danger"
          data-testid="admin-support-revoke-all"
          onClick={() => {
            setOpen(true);
          }}
        >
          {t("admin.support.revokeAll")}
        </Button>
      </div>
      {open ? (
        <Modal
          label={t("admin.support.revokeAllTitle", { tenant: tenant.name })}
          heading={t("admin.support.revokeAllTitle", { tenant: tenant.name })}
          closeLabel={t("admin.support.close")}
          onClose={() => {
            setOpen(false);
          }}
          pending={revoke.isPending}
          testId="admin-revoke-all-dialog"
          className="admin-modal"
        >
          <form
            className="admin-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (matches) revoke.mutate();
            }}
          >
            <p className="admin-card-lead">{t("admin.support.revokeAllBody")}</p>
            <Field
              label={t("admin.support.revokeAllConfirmLabel", { subdomain: tenant.subdomain })}
              wide
            >
              <input
                className="ds-input"
                value={typed}
                autoComplete="off"
                data-testid="admin-revoke-all-typed"
                onChange={(e) => {
                  setTyped(e.target.value);
                }}
              />
            </Field>
            {revoke.isError ? (
              <Banner kind="bad" data-testid="admin-revoke-all-error">
                {supportMessage(revoke.error, t)}
              </Banner>
            ) : null}
            <div className="admin-actions">
              <Button
                type="submit"
                variant="danger"
                disabled={!matches}
                busy={revoke.isPending}
                busyLabel={t("admin.support.ending")}
                data-testid="admin-revoke-all-confirm"
              >
                {t("admin.support.revokeAll")}
              </Button>
              <Button
                onClick={() => {
                  setOpen(false);
                }}
              >
                {t("admin.support.cancel")}
              </Button>
            </div>
          </form>
        </Modal>
      ) : null}
    </Card>
  );
}
